// Better Together — cross-brand "goes well with" recommendations. Pulls REAL,
// non-competitive products from OTHER brands in the playground's entity-db, so
// the module shows authentic complementary items + brand names (a stand-in for a
// "shops like yours also bought" ranking input). Computed once per brand, cached.
import { getAllEntities } from "@faire/prototype-playground/data/get_entity";

import { type ProductCardData } from "./ProductCard";

export interface BetterTogetherOptions {
  /** The target brand's name — excluded from results (no self-recs). */
  brandName: string;
  /** Name-keyword matches that flag a cross-brand product as a competitor
   * (same lane as the target brand) — excluded from results. */
  competitorKeywords: string[];
  /** Name-keyword matches that flag a cross-brand product as complementary
   * (a category shoppers of the target brand would also stock). Prioritized,
   * but not required — an empty list just skips straight to "any non-competing
   * product" once the complementary-first tiers turn up nothing. */
  complementaryKeywords: string[];
  limit?: number;
}

// Keyed by brand name so two different brands rendered in the same session
// (e.g. a side-by-side comparison) each get their own real recommendations
// instead of one brand's cached picks leaking onto another.
const cacheByBrand = new Map<string, ProductCardData[]>();

/** Up to `limit` complementary products, one per brand (for variety), each
 * carrying its real brand name so the tile can read "by {brand}". */
export function getBetterTogether(
  opts: BetterTogetherOptions
): ProductCardData[] {
  const {
    brandName,
    competitorKeywords,
    complementaryKeywords,
    limit = 6,
  } = opts;
  const cached = cacheByBrand.get(brandName);
  if (cached) return cached;

  const all = getAllEntities("product").filter(
    (product) =>
      !!product.imageUrl &&
      !!product.brandName &&
      product.brandName !== brandName
  );
  const notCompetitor = (name: string): boolean =>
    !competitorKeywords.some((keyword) => name.toLowerCase().includes(keyword));
  const isComplementary = (name: string): boolean =>
    complementaryKeywords.some((keyword) =>
      name.toLowerCase().includes(keyword)
    );

  const seenBrands = new Set<string>();
  const seenImages = new Set<string>();
  const picks: ProductCardData[] = [];
  const take = (candidates: typeof all): void => {
    for (const product of candidates) {
      if (picks.length >= limit) break;
      const brandName = product.brandName ?? "";
      // One per brand, and dedupe by image so reused placeholder shots (mock
      // migration data) don't slip in twice.
      if (seenBrands.has(brandName) || seenImages.has(product.imageUrl))
        continue;
      seenBrands.add(brandName);
      seenImages.add(product.imageUrl);
      picks.push({
        token: product.token,
        name: product.name,
        imageUrl: product.imageUrl,
        priceDollars: product.priceDollars,
        msrpDollars: product.msrpDollars,
        brandName,
      });
    }
  };

  // A populated brand avatar is a decent "real import" signal (vs. bare mock
  // rows). Prefer real + complementary, then relax in stages so the module still
  // fills out.
  const real = (p: (typeof all)[number]): boolean => !!p.brandAvatarUrl;
  take(
    all.filter(
      (p) => real(p) && notCompetitor(p.name) && isComplementary(p.name)
    )
  );
  if (picks.length < limit)
    take(all.filter((p) => notCompetitor(p.name) && isComplementary(p.name)));
  if (picks.length < limit)
    take(all.filter((p) => real(p) && notCompetitor(p.name)));
  if (picks.length < limit) take(all.filter((p) => notCompetitor(p.name)));

  cacheByBrand.set(brandName, picks);
  return picks;
}
